<!-- 20260419 -- Copilot (Claude Sonnet 4.6) -- MCP audit log list with per-entry undo -->
<template>
  <div class="mcp-audit-list">
    <!-- Filter bar -->
    <div class="audit-filters mb-3 d-flex flex-wrap align-items-center">
      <select v-model="filterTool" class="custom-select custom-select-sm mr-2" style="max-width:200px" @change="reload">
        <option value="">All tools</option>
        <option v-for="t in TOOL_OPTIONS" :key="t" :value="t">{{ t }}</option>
      </select>
      <button class="btn btn-sm btn-outline-secondary" @click="reload">
        <i class="fa fa-refresh"></i> Refresh
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-4 text-muted">
      <span class="spinner-border spinner-border-sm me-2"></span> Loading audit log…
    </div>

    <!-- Empty -->
    <div v-else-if="entries.length === 0" class="text-muted text-center py-4">
      No audit entries found.
    </div>

    <!-- Entry list -->
    <div v-else>
      <div
        v-for="entry in entries"
        :key="entry._id"
        class="audit-entry mb-4 p-3 border rounded"
        :class="{ 'audit-entry--undone': entry.undone }"
      >
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <!-- 20260419 -- Copilot (Claude Sonnet 4.6) -- Fix: audit entries use tool_name, not tool -->
            <span :class="['badge', 'tool-badge', 'mr-2', toolBadgeClass(entry.tool_name)]">{{ entry.tool_name }}</span>
            <span class="text-muted small">{{ formatTs(entry.timestamp) }}</span>
            <span v-if="entry.client_name" class="text-muted small ml-2">· {{ entry.client_name }}</span>
            <span v-if="entry.dry_run" class="badge badge--read ml-2">dry run</span>
            <span v-if="entry.undone" class="badge bg-warning ml-2">undone</span>
          </div>
          <div class="d-flex">
            <button
              v-if="!entry.undone && canUndo(entry)"
              class="btn btn-sm btn-outline-danger mr-1"
              :disabled="undoingId === entry._id"
              @click="undoEntry(entry)"
            >
              <span v-if="undoingId === entry._id" class="spinner-border spinner-border-sm"></span>
              <span v-else>Undo</span>
            </button>
            <button
              v-if="!entry.undone && entry.request_id && canUndo(entry)"
              class="btn btn-sm btn-danger"
              :disabled="undoingRequest === entry.request_id"
              @click="undoRequest(entry.request_id)"
              title="Undo all changes from this request"
            >
              <span v-if="undoingRequest === entry.request_id" class="spinner-border spinner-border-sm"></span>
              <span v-else>Undo All</span>
            </button>
          </div>
        </div>

        <div class="mt-2">
          <!-- 20260419 -- Copilot (Claude Sonnet 4.6) -- Fix: use affected_entity_ids array, not task_id/affected_count -->
          <small class="text-muted">
            Task: <code>{{ (entry.affected_entity_ids && entry.affected_entity_ids[0]) || '—' }}</code>
            <span v-if="entry.affected_entity_ids && entry.affected_entity_ids.length > 1"> (+{{ entry.affected_entity_ids.length - 1 }} more)</span>
          </small>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="nextCursor" class="text-center mt-3">
        <button class="btn btn-sm btn-outline-primary" :disabled="loadingMore" @click="loadMore">
          <span v-if="loadingMore" class="spinner-border spinner-border-sm me-1"></span>
          Load more
        </button>
      </div>
    </div>

    <!-- Inline error -->
    <div v-if="errorMsg" class="alert alert-danger mt-2 py-2 small">{{ errorMsg }}</div>
  </div>
</template>

<script>
// 20260419 -- Copilot (Claude Sonnet 4.6) -- McpAuditList component logic
import McpService from '@/services/McpService'
import Logger from '@/utils/Logger'

const log = Logger.create('McpAuditList')

const TOOL_OPTIONS = [
  'quick_add','create_task','create_tasks_batch',
  'update_task','update_tasks_batch',
  'complete_task','complete_tasks_batch',
  'delete_task','delete_tasks_batch',
  'move_task','move_tasks_batch',
  'promote_to_someday','defer_task','split_task','reschedule',
]

// 30-day undo window (matches toolTemplate.js free tier; show button regardless of tier)
const UNDO_WINDOW_MS = 30 * 24 * 60 * 60 * 1000

export default {
  name: 'McpAuditList',

  props: {
    token: { type: String, required: true },
  },

  data() {
    return {
      entries:       [],
      nextCursor:    null,
      filterTool:    '',
      loading:       false,
      loadingMore:   false,
      undoingId:     null,
      undoingRequest: null,
      errorMsg:      '',
      TOOL_OPTIONS,
    }
  },

  created() {
    this.reload()
  },

  methods: {
    async reload() {
      this.entries   = []
      this.nextCursor = null
      this.errorMsg  = ''
      this.loading   = true
      try {
        // 20260419 -- Copilot (Claude Sonnet 4.6) -- Fix: API filter param is tool_name; cursor field is next_cursor
        const params = { limit: 25 }
        if (this.filterTool) params.tool_name = this.filterTool
        const res = await McpService.getAuditLog(this.token, params)
        this.entries    = res.data?.data?.entries || []
        this.nextCursor = res.data?.data?.next_cursor || null
      } catch (e) {
        log.error('reload failed', e)
        this.errorMsg = 'Failed to load audit log.'
      } finally {
        this.loading = false
      }
    },

    async loadMore() {
      if (!this.nextCursor || this.loadingMore) return
      this.loadingMore = true
      try {
        const params = { limit: 25, cursor: this.nextCursor }
        if (this.filterTool) params.tool_name = this.filterTool
        const res = await McpService.getAuditLog(this.token, params)
        const more = res.data?.data?.entries || []
        this.entries    = [...this.entries, ...more]
        this.nextCursor = res.data?.data?.next_cursor || null
      } catch (e) {
        log.error('loadMore failed', e)
        this.errorMsg = 'Failed to load more entries.'
      } finally {
        this.loadingMore = false
      }
    },

    canUndo(entry) {
      return Date.now() - entry.timestamp < UNDO_WINDOW_MS
    },

    async undoEntry(entry) {
      this.undoingId  = entry._id
      this.errorMsg   = ''
      try {
        await McpService.undoEntry(entry._id, this.token)
        entry.undone = true
      } catch (e) {
        log.error('undoEntry failed', e)
        // 20260419 -- Copilot (Claude Sonnet 4.6) -- v3 returns { successful, message }; check HTTP status for conflict
        if (e.response?.status === 409) {
          this.errorMsg = 'Cannot undo: the task has been modified since this operation. Undo is no longer safe.'
        } else {
          this.errorMsg = e.response?.data?.message || 'Undo failed.'
        }
      } finally {
        this.undoingId = null
      }
    },

    async undoRequest(requestId) {
      this.undoingRequest = requestId
      this.errorMsg       = ''
      try {
        await McpService.undoRequest(requestId, this.token)
        this.entries.forEach(e => {
          if (e.request_id === requestId) e.undone = true
        })
      } catch (e) {
        log.error('undoRequest failed', e)
        // 20260419 -- Copilot (Claude Sonnet 4.6) -- v3 returns { successful, message }; check HTTP status for conflict
        if (e.response?.status === 409) {
          this.errorMsg = 'Cannot undo: one or more tasks have been modified since this operation.'
        } else {
          this.errorMsg = e.response?.data?.message || 'Undo all failed.'
        }
      } finally {
        this.undoingRequest = null
      }
    },

    formatTs(ms) {
      if (!ms) return '—'
      return new Date(ms).toLocaleString()
    },

    // 20260419 -- Copilot (Claude Sonnet 4.6) -- Classify read tools as green (was: everything non-delete = blue)
    toolBadgeClass(toolName) {
      if (!toolName) return 'badge--audit'
      if (['delete_task', 'delete_tasks_batch'].includes(toolName)) return 'badge--delete'
      // Read tools follow get_* / list_* / search_* naming convention
      if (/^(get_|list_|search_)/.test(toolName)) return 'badge--read'
      return 'badge--write'
    },
  },
}
</script>

<style scoped>
.audit-entry {
  background: #fff;
  transition: opacity 0.2s;
}
.audit-entry--undone {
  opacity: 0.55;
  background: #f8f9fa;
}
.audit-filters .custom-select {
  min-width: 140px;
}
.tool-badge {
  font-weight: 400;
}

/* Shared scope/tool color palette — mirrors McpSettings.vue */
.badge--read   { background-color: #d1f5ec; color: #0e7052; border: 1px solid #a8e8d6; }
.badge--write  { background-color: #dde9ff; color: #1a4fbf; border: 1px solid #b6d0ff; }
.badge--delete { background-color: #ffe8d0; color: #c0520a; border: 1px solid #ffd0a0; }
.badge--audit  { background-color: #f0f2f4; color: #6c757d; border: 1px solid #dee2e6; }
</style>
